/**
 * /admin/reservations/:venueSlug — single-venue bookings dashboard.
 *
 * Privacy posture: shows same-venue history ONLY. Cross-venue agency
 * drill-down lives in a separate (future) endpoint that aggregates without
 * exposing other-venue contact details.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import "./RestaurantAdminPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const STATUSES = ["confirmed", "seated", "completed", "no_show", "cancelled"];
const STATUS_LABELS = {
  confirmed: "Confirmed", seated: "Seated", completed: "Completed",
  no_show: "No-show", cancelled: "Cancelled",
};

const fmtDateTime = (iso) => new Date(iso).toLocaleString("en-AU", {
  weekday: "short", day: "numeric", month: "short",
  hour: "numeric", minute: "2-digit", hour12: true,
  timeZone: "Australia/Brisbane",
});
const sameLocalDate = (iso, dateStr) => {
  // dateStr is YYYY-MM-DD in Brisbane local time
  const briz = new Date(iso).toLocaleString("en-CA", {
    timeZone: "Australia/Brisbane", year: "numeric", month: "2-digit", day: "2-digit",
  });
  return briz === dateStr;
};

export default function RestaurantAdminPage() {
  const { venueSlug } = useParams();
  const [venue, setVenue] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("today");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: v }, { data: r }] = await Promise.all([
        axios.get(`${API}/api/reservations/venues/${venueSlug}`),
        axios.get(`${API}/api/reservations/by-venue/${venueSlug}`),
      ]);
      setVenue(v);
      setItems(r.items || []);
    } catch (e) {
      setItems([]);
    }
  }, [venueSlug]);

  useEffect(() => { load(); }, [load]);

  const todayBrisbane = new Date().toLocaleString("en-CA", {
    timeZone: "Australia/Brisbane", year: "numeric", month: "2-digit", day: "2-digit",
  });

  const filtered = items.filter((b) => {
    if (filter === "today")     return sameLocalDate(b.arrive_by_iso, todayBrisbane);
    if (filter === "upcoming")  return new Date(b.arrive_by_iso) >= new Date() && b.status !== "cancelled";
    if (filter === "flagged")   return b.repeat_offender_at_venue;
    return true;
  });

  const counts = {
    today:    items.filter((b) => sameLocalDate(b.arrive_by_iso, todayBrisbane)).length,
    upcoming: items.filter((b) => new Date(b.arrive_by_iso) >= new Date() && b.status !== "cancelled").length,
    flagged:  items.filter((b) => b.repeat_offender_at_venue).length,
    all:      items.length,
  };

  const changeStatus = async (id, status) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/api/reservations/${id}/status`, { status });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (!venue) {
    return <div style={{ padding: 40 }}>Loading…</div>;
  }

  const accent = venue.accent_color || "#C75B3D";

  return (
    <div className="ra-wrap" style={{ "--ra-accent": accent }}>
      <header className="ra-head">
        <div>
          <div className="ra-eyebrow">Restaurant admin · privacy-safe</div>
          <h1>{venue.name}</h1>
          <p className="ra-sub">{venue.address} · {venue.hours}</p>
        </div>
        <Link to={`/reserve/${venueSlug}`} className="ra-btn ra-btn--ghost">+ New booking</Link>
      </header>

      <div className="ra-filters">
        {[
          { key: "today",    label: "Today"        },
          { key: "upcoming", label: "Upcoming"     },
          { key: "flagged",  label: "⚠ Flagged"   },
          { key: "all",      label: "All bookings" },
        ].map((f) => (
          <button
            key={f.key}
            className={`ra-chip ${filter === f.key ? "is-active" : ""}`}
            onClick={() => setFilter(f.key)}
            data-testid={`ra-filter-${f.key}`}
          >
            {f.label}
            <span className="ra-chip__count">{counts[f.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="ra-empty">No bookings in this filter.</div>
      ) : (
        <div className="ra-table-wrap" data-testid="ra-bookings-table">
          <table className="ra-table">
            <thead>
              <tr>
                <th>Arrive by</th>
                <th>Guest</th>
                <th>Party</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className={b.status === "cancelled" ? "is-cancelled" : ""}>
                  <td>
                    <div className="ra-when">{fmtDateTime(b.arrive_by_iso)}</div>
                    <div className="ra-id">{b.id}</div>
                  </td>
                  <td>
                    <div className="ra-name">
                      {b.name}
                      {b.repeat_offender_at_venue && (
                        <span
                          className="ra-flag"
                          title={`At this venue: ${b.same_venue_history.no_show} no-show · ${b.same_venue_history.cancelled} cancelled — consider deposit`}
                          data-testid={`ra-flag-${b.id}`}
                        >
                          ⚠ Repeat
                        </span>
                      )}
                    </div>
                    {b.same_venue_history?.total > 1 && (
                      <div className="ra-history">
                        {b.same_venue_history.total}× here · {b.same_venue_history.no_show} no-show · {b.same_venue_history.cancelled} cancelled
                      </div>
                    )}
                  </td>
                  <td><strong>{b.party_size}</strong></td>
                  <td>
                    <div>{b.email}</div>
                    <div className="ra-phone">{b.phone}</div>
                  </td>
                  <td>
                    <select
                      value={b.status}
                      disabled={busyId === b.id}
                      onChange={(e) => changeStatus(b.id, e.target.value)}
                      className={`ra-status ra-status--${b.status}`}
                      data-testid={`ra-status-${b.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="ra-notes">{b.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="ra-foot">
        <div className="ra-priv">
          <strong>🔒 Privacy notice:</strong> repeat-offender flags use this venue's history only.
          Aggregate cross-venue intelligence is available to agency admins via a
          separate role-gated dashboard (coming soon) — diner contact details
          are never shared between venues.
        </div>
      </div>
    </div>
  );
}
